import { css } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';
import type IThemeColor from '@inter-ikea-kompis/types/lib/types/theme/IThemeColor.js';

import { FocusRing, Text } from '@ingka/common-styles-webc';
import * as skapaColors from '@ingka/variables/colours-css';

import ProductRowOptionalStateEnum from '../../enums/ProductRowOptionalStateEnum.js';

const IMAGE_SIZE = 64;

export default class ProductRowStyle {
	public static getStyle(options: {
		theme: ITheme;
		isMobile: boolean;
		showStockStatusOnPrint: boolean;
		optionalState: ProductRowOptionalStateEnum;
		hasDualCurrencies: boolean;
		isDiscontinued: boolean;
		isSpr: boolean;
	}): string {
		const {
			theme,
			optionalState,
			isDiscontinued,
			hasDualCurrencies,
			isMobile,
			showStockStatusOnPrint,
			isSpr
		} = options;
		const { spacing, color, breakpoint } = theme;
		const { headingXSmall } = breakpoint.small.fontStyle;

		return css`
			${Text.CSS}
			${FocusRing.CSS}

			:host {
				display: block;
				page-break-inside: avoid;
				break-inside: avoid;
			}

			p {
				margin: 0;
			}

			.visuallyHidden {
				border: 0 !important;
				clip: rect(${1 / 16}rem, ${1 / 16}rem, ${1 / 16}rem, ${1 / 16}rem) !important;
				-webkit-clip-path: inset(50%) !important;
				clip-path: inset(50%) !important;
				height: ${1 / 16}rem !important;
				margin: -${1 / 16}rem !important;
				overflow: hidden !important;
				padding: 0 !important;
				position: absolute !important;
				width: ${1 / 16}rem !important;
				white-space: nowrap !important;
				top: 0;
				bottom: 0;
				left: 0;
				right: 0;
				-webkit-user-select: none;
				user-select: none;
			}

			li {
				list-style-type: none;
			}
			ul {
				margin: 0;
				padding: 0;
				flex: 1;
			}

			a.productContent {
				display: block;
			}

			.productContent {
				text-decoration: none;
				position: relative;
				cursor: pointer;
			}

			.productName {
				font-weight: bold;
				color: ${color.text.dark};
			}

			.productContent:hover .productName {
				text-decoration: underline;
			}

			.mainProduct .row {
				display: grid;
				grid-gap: 0 ${spacing.space100 / 16}rem;
				grid-template-columns: ${IMAGE_SIZE / 16}rem 2.5fr min-content ${optionalState !==
						ProductRowOptionalStateEnum.addable &&
					optionalState !== ProductRowOptionalStateEnum.removableFromTab
						? '1fr'
						: ''};
				padding-bottom: ${spacing.space100 / 16}rem;
			}

			.mainProduct .row:last-child {
				padding-bottom: 0;
			}

			.mainProduct .row .quantityContainer {
				display: flex;
				align-items: ${optionalState === ProductRowOptionalStateEnum.removableFromList
					? 'center'
					: 'start'};
			}

			.checkboxLabel {
				margin-right: 1rem;
				margin-left: 1rem;
				margin-top: ${2 / 16}rem;
				font-size: ${headingXSmall.size / 16}rem;
				color: ${color.text.default};
			}

			.quantity {
				white-space: nowrap;
				font-weight: bold;
			}

			.selfServeLocationContainer {
				display: flex;
			}

			.mainProduct .row > *:last-child {
				display: flex;
				flex-direction: column;
			}

			.mainProduct .row:first-child skapa-product-identifier {
				padding-top: ${spacing.space50 / 16}rem;
			}

			.mainProduct .row > .totalPrice {
				display: flex;
				flex-direction: ${hasDualCurrencies ? 'column' : 'row-reverse'};
				${hasDualCurrencies ? 'align-items: flex-end' : ''};
			}

			.mainProduct .availabilityStatusIndicators,
			.includedProduct .availabilityStatusIndicators {
				margin-top: ${spacing.space50 / 16}rem;
			}

			.includedProducts {
				background-color: ${color.background.alt};
				margin-top: ${spacing.space150 / 16}rem;
			}

			.includedProducts .includedProductsExpander {
				padding: ${spacing.space50 / 16}rem ${spacing.space75 / 16}rem;
			}

			.includedProducts .spaceContainer {
				display: flex;
			}

			.includedProducts .spaceContainer > div:first-child {
				width: ${spacing.space200 / 16}rem;
				flex-shrink: 0;
				flex-grow: 0;
			}

			.includedProducts .spaceContainer > div:last-child {
				flex-shrink: 1;
				flex-grow: 1;
				padding: ${spacing.space75 / 16}rem 0;
			}

			.includedProducts .includedProduct {
				padding: ${spacing.space75 / 16}rem ${spacing.space50 / 16}rem;
			}

			.includedProducts .includedProduct .row {
				display: grid;
				grid-gap: ${spacing.space100 / 16}rem;
				grid-template-columns: 1fr min-content;
				padding-bottom: ${spacing.space50 / 16}rem;
			}

			.includedProducts .includedProduct .row:last-child {
				padding-bottom: 0;
			}

			.mainProduct.discontinued .productName,
			.includedProduct.discontinued .productName,
			.mainProduct.discontinued .productDescription,
			.includedProduct.discontinued .productDescription,
			.mainProduct.discontinued .productImage,
			.includedProduct.discontinued .productImage,
			.mainProduct.discontinued .quantity {
				opacity: 0.4;
			}

			.productImage {
				height: fit-content;
			}

			.productImageEventOnly {
				height: fit-content;
			}

			.productImageEventOnly:hover {
				cursor: pointer;
			}

			.includedProducts .includedProduct .collect,
			.mainProduct .collect {
				display: flex;
				align-items: flex-end;
				justify-content: flex-end;
				white-space: nowrap;
			}

			.mainProduct .row .removeFromListButton {
				padding: 0 ${spacing.space125 / 16}rem;
			}

			.productDescription {
				color: ${color.text.default};
			}

			.includedProductsExpander {
				cursor: pointer;
			}

			.totalPrice kompis-text,
			.quantity p {
				color: ${color.text.dark};
			}

			.totalPrice kompis-text:nth-child(odd):not(:last-child) {
				margin-bottom: ${spacing.space25 / 16}rem;
			}

			${isDiscontinued ? this.getDisabledStyle(isSpr, color) : ''}
			${isMobile ? this.getMobileStyle(theme) : ''}
			${this.getPrintStyle(theme, showStockStatusOnPrint)}

			.locationLabel {
				margin-bottom: ${spacing.space50 / 16}rem;
				color: ${color.text.default};
			}

			.locationText {
				font-weight: bold;
				margin-top: ${3 / 20}rem;
				color: ${color.text.dark};
			}

			.productIdentifiers {
				display: flex;
			}

			.locationContainer {
				display: flex;
				flex-direction: column;
			}

			.productIdentifierDivider {
				width: ${spacing.space75 / 16}rem;
				flex-shrink: 0;
			}

			.includedProduct .articleNumber.subtle skapa-product-identifier {
				border: ${1 / 16}rem solid ${skapaColors.colourNeutral5};
			}

			${options.isMobile ? this.getMobileStyle(theme) : ''}
			${this.getPrintStyle(theme, showStockStatusOnPrint)}

			.checkboxWrapper {
				display: flex;
			}

			skapa-status:not(:last-child) {
				margin-bottom: ${spacing.space50 / 16}rem;
			}

			kompis-do-not-diy {
				margin-top: ${spacing.space25 / 16}rem;
			}
		`;
	}

	private static getDisabledStyle(isSpr = false, color: IThemeColor): string {
		const fontColor = isSpr ? color.disabled.default : color.neutral.white;
		const backgroundColor = isSpr ? color.background.alt : color.disabled.default;

		return css`
			skapa-product-identifier::after {
				color: ${fontColor};
				background-color: ${backgroundColor};
				border-color: ${backgroundColor};
			}
		`;
	}

	private static getMobileStyle(theme: ITheme): string {
		const { spacing } = theme;

		return css`
			.mainProduct .row {
				grid-template-columns: ${IMAGE_SIZE / 16}rem 2fr;
				grid-gap: 0 ${spacing.space100 / 16}rem;
			}

			.mainProduct > *:last-child {
				padding-bottom: 0;
			}

			.mainProduct .row > .totalPrice {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}

			.mainProduct kompis-collapsible {
				display: block;
			}

			.includedProducts {
				margin-top: ${spacing.space100 / 16}rem;
			}

			.includedProducts .includedProduct .row {
				display: block;
			}

			.mainProduct .addButton,
			.mainProduct .removeFromTabButton {
				display: flex;
				align-items: flex-end;
				justify-content: flex-end;
			}
		`;
	}

	private static getPrintStyle(theme: ITheme, showStockStatusOnPrint: boolean): string {
		const { color, spacing } = theme;

		return css`
			@media print {
				.mainProduct .row {
					grid-gap: ${spacing.space100 / 16}rem ${spacing.space100 / 16}rem;
				}

				.includedProducts .includedProduct,
				.includedProducts .includedProduct .row:last-child {
					padding-bottom: 0;
				}

				skapa-status {
					display: ${showStockStatusOnPrint ? 'flex' : 'none'};
				}

				.includedProducts {
					background-color: ${color.background.default};
					margin-top: ${spacing.space100 / 16}rem;
					padding: 0;
				}
			}
		`;
	}
}
